import { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
} from "recharts";
import { CHART_COLORS, PROMPT_LEVELS, fmtDate, promptInfo } from "@/lib/aba";
import { CHART } from "@/constants/testIds";

const AXIS_TICK = { fontSize: 12, fill: "#64748B" };

/**
 * Custom data point: independent responses are filled dots; prompted responses are
 * open dots with the prompt code letter (V, G, M, F) inside.
 */
const makePromptDot = (color) => (props) => {
  const { cx, cy, payload, value, index } = props;
  if (value === null || value === undefined || cx === undefined || cy === undefined) return null;
  const info = promptInfo(payload?.prompt);
  const independent = info.key === "indipendente";
  const key = `dot-${payload?.session_id || index}`;
  if (independent) {
    return <circle key={key} cx={cx} cy={cy} r={4.5} fill={color} stroke="#fff" strokeWidth={1.5} />;
  }
  return (
    <g key={key}>
      <circle cx={cx} cy={cy} r={8} fill="#fff" stroke={info.color} strokeWidth={2} />
      <text x={cx} y={cy + 3.5} textAnchor="middle" fontSize={9} fontWeight={700} fill={info.color}>{info.code}</text>
    </g>
  );
};

/**
 * ABA equal-interval line graph for ONE action only.
 * modes: percent (default) | count | cumulative
 */
export const AbaChart = ({ analytics, mode = "percent", height = 320, animate = false }) => {
  const { points = [], criterio = 80, cambio_fase_numero: phaseNumero } = analytics || {};

  const { data, xMax, phaseX, maxTotale, maxCum } = useMemo(() => {
    const rows = points.map((p) => ({
      ...p,
      pre: p.fase === "introdotto" ? p.percentuale : null,
      post: p.fase === "acquisito" ? p.percentuale : null,
    }));
    const xm = Math.max(points.length, 5);
    return {
      data: rows,
      xMax: xm,
      phaseX: phaseNumero ? phaseNumero - 0.5 : null,
      maxTotale: Math.max(10, ...points.map((p) => p.totale)),
      maxCum: Math.max(10, ...points.map((p) => p.cumulativo_prove || 0)),
    };
  }, [points, phaseNumero]);

  const ticks = Array.from({ length: xMax }, (_, i) => i + 1);
  const xLabel = { value: "Sessioni", position: "insideBottomRight", offset: -4, fontSize: 11, fill: "#64748B" };

  if (mode === "count") {
    return (
      <div data-testid={CHART.container} data-mode="count" className="w-full" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 16, right: 16, left: -8, bottom: 8 }} barCategoryGap="30%">
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 6" vertical={false} />
            <XAxis dataKey="numero" tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} tick={AXIS_TICK} label={xLabel} />
            <YAxis domain={[0, maxTotale]} allowDecimals={false} tickLine={false} axisLine={false} tick={AXIS_TICK} width={44} />
            <Tooltip content={<AbaTooltip />} cursor={{ fill: "#F1F5F9" }} />
            <Bar dataKey="totale" name="Totale prove" fill="#E2E8F0" radius={[4, 4, 0, 0]} isAnimationActive={animate} />
            <Bar dataKey="corrette" name="Corrette" fill={CHART_COLORS.data} radius={[4, 4, 0, 0]} isAnimationActive={animate} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (mode === "cumulative") {
    // Classic cumulative record: the line never goes down; the steeper the slope, the higher the rate.
    return (
      <div data-testid={CHART.container} data-mode="cumulative" className="w-full" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 20, right: 20, left: -8, bottom: 8 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 6" vertical={false} />
            {phaseX && <ReferenceArea x1={phaseX} x2={xMax + 0.5} y1={0} y2={maxCum} fill="#ECFDF5" fillOpacity={0.6} ifOverflow="hidden" />}
            <XAxis dataKey="numero" type="number" domain={[0.5, xMax + 0.5]} ticks={ticks} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} tick={AXIS_TICK} label={xLabel} />
            <YAxis domain={[0, maxCum]} allowDecimals={false} tickLine={false} axisLine={false} tick={AXIS_TICK} width={48} label={{ value: "Risposte cumulative", angle: -90, position: "insideLeft", offset: 18, fontSize: 11, fill: "#64748B" }} />
            <Tooltip content={<AbaTooltip cumulative />} cursor={{ stroke: CHART_COLORS.grid }} />
            {phaseX && <ReferenceLine x={phaseX} stroke={CHART_COLORS.phase} strokeWidth={2} label={{ value: "Acquisito", position: "top", fill: CHART_COLORS.phase, fontSize: 11, fontWeight: 600 }} />}
            <Line type="linear" dataKey="cumulativo_prove" name="Prove cumulative" stroke={CHART_COLORS.total} strokeDasharray="4 4" strokeWidth={1.5} dot={false} activeDot={false} isAnimationActive={animate} />
            <Line type="linear" dataKey="cumulativo" name="Corrette cumulative" stroke={CHART_COLORS.data} strokeWidth={2.5} dot={makePromptDot(CHART_COLORS.data)} activeDot={{ r: 6 }} isAnimationActive={animate} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div data-testid={CHART.container} data-mode="percent" className="w-full" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 20, left: -8, bottom: 8 }}>
          <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 6" vertical={false} />
          {phaseX && <ReferenceArea x1={phaseX} x2={xMax + 0.5} y1={0} y2={100} fill="#ECFDF5" fillOpacity={0.6} ifOverflow="hidden" />}
          <XAxis dataKey="numero" type="number" domain={[0.5, xMax + 0.5]} ticks={ticks} tickLine={false} axisLine={{ stroke: CHART_COLORS.grid }} tick={AXIS_TICK} label={xLabel} />
          <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} tick={AXIS_TICK} width={48} />
          <Tooltip content={<AbaTooltip />} cursor={{ stroke: CHART_COLORS.grid }} />
          <ReferenceLine
            y={criterio}
            stroke={CHART_COLORS.criterion}
            strokeDasharray="6 6"
            strokeWidth={2}
            ifOverflow="extendDomain"
            label={{ value: `Criterio ${criterio}%`, position: "insideTopRight", fill: "#B45309", fontSize: 11, fontWeight: 600 }}
          />
          {phaseX && <ReferenceLine x={phaseX} stroke={CHART_COLORS.phase} strokeWidth={2} label={{ value: "Acquisito", position: "top", fill: CHART_COLORS.phase, fontSize: 11, fontWeight: 600 }} />}
          <Line type="linear" dataKey="trend" name="Trend" stroke={CHART_COLORS.trend} strokeDasharray="2 6" strokeWidth={2} dot={false} activeDot={false} isAnimationActive={animate} connectNulls />
          <Line type="linear" dataKey="pre" name="Introdotto" stroke={CHART_COLORS.data} strokeWidth={2.5} dot={makePromptDot(CHART_COLORS.data)} activeDot={{ r: 6 }} connectNulls={false} isAnimationActive={animate} />
          <Line type="linear" dataKey="post" name="Acquisito" stroke={CHART_COLORS.dataAcquired} strokeWidth={2.5} dot={makePromptDot(CHART_COLORS.dataAcquired)} activeDot={{ r: 6 }} connectNulls={false} isAnimationActive={animate} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const AbaTooltip = ({ active, payload, cumulative }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const info = promptInfo(p.prompt);
  return (
    <div className="rounded-lg border border-border bg-popover text-popover-foreground shadow-md px-3 py-2 text-sm min-w-[190px]">
      <p className="font-heading font-semibold">Sessione {p.numero}</p>
      <p className="text-xs text-muted-foreground">{fmtDate(p.data, "EEEE d MMMM yyyy")}</p>
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 tabular">
        <span className="text-muted-foreground">Corrette</span><span className="text-right font-medium">{p.corrette}/{p.totale}</span>
        <span className="text-muted-foreground">Percentuale</span><span className="text-right font-medium">{p.percentuale}%</span>
        {cumulative && (<><span className="text-muted-foreground">Cumulativo</span><span className="text-right font-medium">{p.cumulativo} / {p.cumulativo_prove}</span></>)}
        {!cumulative && p.trend !== null && p.trend !== undefined && (<><span className="text-muted-foreground">Trend</span><span className="text-right">{p.trend}%</span></>)}
        <span className="text-muted-foreground">Prompt</span><span className="text-right font-medium" style={{ color: info.color }}>{info.label}</span>
        <span className="text-muted-foreground">Fase</span><span className={`text-right font-medium ${p.fase === "acquisito" ? "text-emerald-700" : "text-primary"}`}>{p.fase === "acquisito" ? "Acquisito" : "Introdotto"}</span>
      </div>
      {p.note && <p className="mt-2 text-xs text-muted-foreground border-t border-border pt-1.5 max-w-[240px]">{p.note}</p>}
    </div>
  );
};

export const ChartLegend = ({ criterio = 80, hasPhase, mode, promptCounts }) => {
  const usedPrompts = PROMPT_LEVELS.filter((l) => l.key !== "indipendente" && (promptCounts?.[l.key] || 0) > 0);
  return (
    <div className="space-y-1.5">
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {mode === "count" && (
          <>
            <li className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHART_COLORS.data }} /> Corrette</li>
            <li className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-200" /> Totale prove</li>
          </>
        )}
        {mode === "cumulative" && (
          <>
            <li className="flex items-center gap-1.5"><span className="h-0.5 w-4" style={{ background: CHART_COLORS.data }} /> Corrette cumulative</li>
            <li className="flex items-center gap-1.5"><span className="h-0.5 w-4 border-t-2 border-dashed" style={{ borderColor: CHART_COLORS.total }} /> Prove cumulative</li>
            {hasPhase && <li className="flex items-center gap-1.5"><span className="h-3 w-0.5" style={{ background: CHART_COLORS.phase }} /> Cambio fase</li>}
          </>
        )}
        {mode === "percent" && (
          <>
            <li className="flex items-center gap-1.5"><span className="h-0.5 w-4" style={{ background: CHART_COLORS.data }} /> % corrette (introdotto)</li>
            {hasPhase && <li className="flex items-center gap-1.5"><span className="h-0.5 w-4" style={{ background: CHART_COLORS.dataAcquired }} /> % corrette (acquisito)</li>}
            <li className="flex items-center gap-1.5"><span className="h-0.5 w-4 border-t-2 border-dashed" style={{ borderColor: CHART_COLORS.criterion }} /> Criterio {criterio}%</li>
            <li className="flex items-center gap-1.5"><span className="h-0.5 w-4 border-t-2 border-dotted" style={{ borderColor: CHART_COLORS.trend }} /> Linea di tendenza</li>
            {hasPhase && <li className="flex items-center gap-1.5"><span className="h-3 w-0.5" style={{ background: CHART_COLORS.phase }} /> Cambio fase</li>}
          </>
        )}
      </ul>
      {mode !== "count" && (
        <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid="prompt-legend">
          <li className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS.data }} /> punto pieno = indipendente</li>
          {usedPrompts.length > 0 ? usedPrompts.map((l) => (
            <li key={l.key} className="flex items-center gap-1"><span className="h-4 w-4 rounded-full border-2 text-[9px] font-bold flex items-center justify-center bg-white" style={{ borderColor: l.color, color: l.color }}>{l.code}</span> {l.label}</li>
          )) : <li>lettera nel punto = livello di aiuto (V verbale, G gestuale, M modello, F fisico)</li>}
        </ul>
      )}
    </div>
  );
};
